import React, { useEffect, useState } from "react";
import {
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Switch,
  Grid,
  Box,
  Button,
  Dialog,
  TextField,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  DialogContentText,
  CircularProgress,
  Backdrop,
  Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DebugPanel from "./DebugPanel";
import OnboardingDebugPanel from "../OnboardingDebugPanel";
import FontController from "./Font Controller/FontController";
import billingApiService from "../../services/api/BillingApiService";
import settingsApiService from "../../services/api/SettingsApiService";
import { useAuthStore } from "../../stores/zustand";

/**
 * UserAccountPopover Component
 *
 * Provides user account management functionality including:
 * - Language toggle
 * - Push notification status and controls
 * - Push notification debug panel (development only)
 * - Test local platform notifications (Electron only)
 * - Password change
 * - External links (license, FAQ)
 * - Logout
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the popover is open
 * @param {Element} props.anchorEl - Anchor element for the popover
 * @param {Function} props.onClose - Callback when popover is closed
 * @param {Function} props.onLanguageToggle - Callback for language toggle
 * @param {Function} props.onPasswordUpdate - Callback for password update
 * @param {Function} props.onLogout - Callback for logout
 * @param {Function} props.onTestNotifications - Callback to navigate to test notifications page
 * @param {string} props.currentLanguage - Current language code
 * @param {string} props.userEmail - User's email address
 * @param {string} props.notificationPermission - Current notification permission status
 * @param {Function} props.onEnableNotifications - Callback to enable push notifications
 * @param {boolean} props.isElectron - Whether running in Electron environment
 * @returns {JSX.Element} UserAccountPopover component
 */
const UserAccountPopover = ({
  open,
  anchorEl,
  onClose,
  onLanguageToggle,
  onPasswordUpdate,
  onLogout,
  onTestNotifications,
  currentLanguage,
  userEmail,
  notificationPermission = "default",
  onEnableNotifications,
  isElectron = false,
  isCheckoutPage = false,
}) => {
  const { t } = useTranslation();
  const { currentProduct, user } = useAuthStore();
  const isPhysioTherapist = currentProduct === "physio_therapist";
  const isPhysioUser = currentProduct === "physio_user";

  const nameParts = (user?.name || "").split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Debug panel dialog state
  const [debugDialogOpen, setDebugDialogOpen] = useState(false);
  const [onboardingDebugDialogOpen, setOnboardingDebugDialogOpen] =
    useState(false);
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [physioUserDialogOpen, setPhysioUserDialogOpen] = useState(false);

  // Snooze toggle state
  const [isSnoozed, setIsSnoozed] = useState(false);
  const [loadingSnooze, setLoadingSnooze] = useState(true);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [pendingSnoozeState, setPendingSnoozeState] = useState(null); // true = snooze, false = unsnooze

  // Snackbar state for snooze actions
  const [snoozeSuccess, setSnoozeSuccess] = useState("");
  const [snoozeError, setSnoozeError] = useState("");

  // Fetch snooze status (not applicable for physio therapists)
  useEffect(() => {
    if (isPhysioTherapist) {
      setLoadingSnooze(false);
      return;
    }
    (async () => {
      try {
        const enabled = await settingsApiService.areNotificationsSnoozed();
        setIsSnoozed(enabled);
      } catch (e) {
        console.error("failed to fetch snooze state", e);
      } finally {
        setLoadingSnooze(false);
      }
    })();
  }, [isPhysioTherapist]);

  /**
   * Opens password change dialog
   */
  const handlePasswordDialogOpen = () => {
    setPasswordDialogOpen(true);
    setPasswordError("");
    setPasswordSuccess("");
  };

  /**
   * Closes password change dialog and resets form
   */
  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  /**
   * Opens debug panel dialog
   */
  const handleDebugDialogOpen = () => {
    setDebugDialogOpen(true);
  };

  /**
   * Closes debug panel dialog
   */
  const handleDebugDialogClose = () => {
    setDebugDialogOpen(false);
  };

  /**
   * Opens onboarding debug panel dialog
   */
  const handleOnboardingDebugDialogOpen = () => {
    setOnboardingDebugDialogOpen(true);
  };

  /**
   * Closes onboarding debug panel dialog
   */
  const handleOnboardingDebugDialogClose = () => {
    setOnboardingDebugDialogOpen(false);
  };

  /**
   * Handles password form submission
   */
  const handlePasswordSubmit = async () => {
    setPasswordError("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t("allFieldsRequired"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("passwordsDoNotMatch"));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t("passwordTooShort"));
      return;
    }

    setPasswordLoading(true);

    try {
      const result = await onPasswordUpdate(currentPassword, newPassword);

      if (result.success) {
        setPasswordSuccess(result.message);
        setTimeout(() => {
          handlePasswordDialogClose();
        }, 2000);
      } else {
        setPasswordError(result.message);
      }
    } catch (error) {
      setPasswordError(t("unexpectedError"));
    } finally {
      setPasswordLoading(false);
    }
  };

  /**
   * Handles external link clicks
   */
  const handleExternalLink = (url) => {
    window.open(url, "_blank");
  };

  /**
   * Handles logout with confirmation
   */
  const handleLogoutClick = () => {
    if (window.confirm(t("confirmLogout"))) {
      onLogout();
    }
  };

  // Open Stripe Billing Portal via backend (cookie-based)
  const handleOpenBillingPortal = async () => {
    if (isPhysioUser) {
      setPhysioUserDialogOpen(true);
      return;
    }
    try {
      const res = await billingApiService.openBillingPortal();
      if (res?.url) {
        window.location.assign(res.url);
        return;
      }
      setPortalDialogOpen(true);
      console.error("[BillingPortal] No url returned");
    } catch (e) {
      setPortalDialogOpen(true);
      console.error("[BillingPortal] Failed to open", e);
    }
  };

  /**
   * Handle snooze toggle
   */
  const handleSnoozeToggle = (e) => {
    const next = e.target.checked;
    setPendingSnoozeState(next);
    setSnoozeDialogOpen(true);
  };

  const closeSnoozeDialog = () => {
    setSnoozeDialogOpen(false);
    setPendingSnoozeState(null);
  };

  const handleConfirmSnoozeChange = async () => {
    setLoadingSnooze(true);
    try {
      if (pendingSnoozeState) {
        await settingsApiService.snoozeNotifications();
        setSnoozeSuccess(t("notificationsHaveBeenSnoozed"));
      } else {
        await settingsApiService.unsnoozeNotifications();
        setSnoozeSuccess(t("notificationsHaveBeenUnsnoozed"));
      }
      setIsSnoozed(pendingSnoozeState);
    } catch (e) {
      console.error("Failed to update snooze", e);
      setSnoozeError(t("settingsErrorOccurred"));
    } finally {
      setLoadingSnooze(false);
      closeSnoozeDialog();
    }
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          ".MuiPaper-root": {
            width: "280px",
            maxWidth: "100%",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
          <Typography variant="subtitle1" fontWeight="600">
            {t("privateSettings")}
          </Typography>
          {userEmail && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {userEmail}
            </Typography>
          )}
        </Box>

        {/* Language Toggle */}
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="body2">EN</Typography>
            </Grid>
            <Grid item>
              <Switch
                checked={currentLanguage === "de"}
                onChange={onLanguageToggle}
                size="small"
              />
            </Grid>
            <Grid item>
              <Typography variant="body2">DE</Typography>
            </Grid>
          </Grid>
        </Box>
        {/* Snooze Notifications */}
        {!isCheckoutPage && !isPhysioTherapist && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
            <FormControlLabel
              control={
                <Switch
                  className="snoozeSwitch"
                  onChange={handleSnoozeToggle}
                  disabled={loadingSnooze}
                  checked={isSnoozed}
                  color="secondary"
                />
              }
              label={
                <Typography variant="subtitle2" color="text.secondary">
                  {t("snoozeEnabled")}
                </Typography>
              }
            />
          </Box>
        )}
        {/* TextSize Controller*/}
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
          <Typography sx={{ fontSize: "15px" }}>
            {t('font_size_controller')}
          </Typography>
          <FontController />
        </Box>

        {/* Push Notifications Status */}
        {!isCheckoutPage && !isElectron && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor:
                    notificationPermission === "granted"
                      ? "#4caf50"
                      : "#f44336",
                  mr: 1,
                }}
              />
              <Typography variant="body2" fontWeight="500">
                {t("pushNotifications")}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 1, display: "block" }}
            >
              {notificationPermission === "granted"
                ? t("notificationsEnabled")
                : t("notificationsDisabled")}
            </Typography>
            {notificationPermission !== "granted" && (
              <Button
                size="small"
                variant="outlined"
                onClick={onEnableNotifications}
                sx={{ mt: 1 }}
                fullWidth
              >
                {t("enableNotifications")}
              </Button>
            )}
          </Box>
        )}

        {/* Menu Items */}
        {!isCheckoutPage && (
          <List component="nav" dense>
            <ListItem button onClick={handleOpenBillingPortal}>
              <ListItemText primary={t("paymentmanagement")} />
            </ListItem>

            <ListItem
              button
              onClick={() =>
                handleExternalLink(
                  `${process.env.REACT_APP_FIREBASE_BASE_URL}/terms`
                )
              }
            >
              <ListItemText primary={t("license")} />
            </ListItem>

            <ListItem button onClick={handlePasswordDialogOpen}>
              <ListItemText primary={t("password")} />
            </ListItem>

            <ListItem
              button
              onClick={() =>
                handleExternalLink(
                  `${process.env.REACT_APP_FIREBASE_BASE_URL}/help-faq`
                )
              }
            >
              <ListItemText primary={t("faq")} />
            </ListItem>

            {/* Test Local Notifications - Only in Electron */}
            {isElectron && (
              <ListItem
                button
                onClick={() => {
                  onTestNotifications && onTestNotifications();
                  onClose();
                }}
              >
                <ListItemText
                  primary="Test Local Platform Notifications"
                  secondary="Test OS notification system"
                  sx={{
                    "& .MuiListItemText-primary": {
                      color: "primary.main",
                      fontWeight: "medium",
                    },
                    "& .MuiListItemText-secondary": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </ListItem>
            )}

            {/* Debug Panels - Only in development */}
            {process.env.NODE_ENV === "development" && (
              <>
                <ListItem button onClick={handleDebugDialogOpen}>
                  <ListItemText primary="Push Notification Debug" />
                </ListItem>
                <ListItem button onClick={handleOnboardingDebugDialogOpen}>
                  <ListItemText primary="Onboarding Debug" />
                </ListItem>
              </>
            )}

            <ListItem button onClick={handleLogoutClick}>
              <ListItemText
                primary={t("logOut")}
                sx={{
                  "& .MuiListItemText-primary": {
                    color: "error.main",
                    fontWeight: "medium",
                  },
                }}
              />
            </ListItem>
          </List>
        )}

        {isCheckoutPage && (
          <List>
            <ListItem button onClick={handleLogoutClick}>
              <ListItemText
                primary={t("logOut")}
                sx={{
                  "& .MuiListItemText-primary": {
                    color: "error.main",
                    fontWeight: "medium",
                  },
                }}
              />
            </ListItem>
          </List>
        )}

        {/* Close Button */}
        <Box
          sx={{
            textAlign: "center",
            p: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <Button onClick={onClose} size="small">
            {t("close")}
          </Button>
        </Box>
      </Popover>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("changePassword")}
          </Typography>

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <TextField
              label={t("currentPassword")}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              disabled={passwordLoading}
              autoComplete="current-password"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label={t("newPassword")}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              disabled={passwordLoading}
              autoComplete="new-password"
              helperText={t("passwordMinLength")}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              label={t("confirmPassword")}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              disabled={passwordLoading}
              autoComplete="new-password"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={handlePasswordDialogClose}
              disabled={passwordLoading}
              sx={{ '&:hover': { bgcolor: 'error.main', color: 'white' } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handlePasswordSubmit}
              variant="contained"
              disabled={passwordLoading}
            >
              {passwordLoading ? t("updating") : t("updatePassword")}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Debug Panel Dialogs */}
      {process.env.NODE_ENV === "development" && (
        <>
          {/* Push Notification Debug Dialog */}
          <Dialog
            open={debugDialogOpen}
            onClose={handleDebugDialogClose}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Push Notification Debug</DialogTitle>
            <DialogContent>
              <DebugPanel />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDebugDialogClose}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Onboarding Debug Dialog */}
          <Dialog
            open={onboardingDebugDialogOpen}
            onClose={handleOnboardingDebugDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>🔧 Onboarding Debug Panel</DialogTitle>
            <DialogContent>
              <OnboardingDebugPanel />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleOnboardingDebugDialogClose}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {/* Physio User — free access info dialog */}
      <Dialog
        open={physioUserDialogOpen}
        onClose={() => setPhysioUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("paymentmanagement")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("customer")}: {lastName}{lastName && firstName ? ", " : ""}{firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("subscriptionType")}: {t("physioUserSubscriptionType")}
          </Typography>
          <Typography variant="body1">
            {t("physioUserFreeAccess")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setPhysioUserDialogOpen(false)}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={portalDialogOpen}
        onClose={() => setPortalDialogOpen(false)}
      >
        <DialogTitle>{t("baseError")}</DialogTitle>
        <DialogContent>
          <Typography>{t("baseErrorOccurred")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setPortalDialogOpen(false)}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
      {!isPhysioTherapist && (
        <>
          <Dialog open={snoozeDialogOpen} onClose={closeSnoozeDialog}>
            <DialogTitle>
              {pendingSnoozeState
                ? t("exerciseSnoozeConfirmation")
                : t("exerciseUnsnoozeConfirmation")}
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <DialogContentText>
                {pendingSnoozeState
                  ? t("exerciseSnoozeConfirmationMessage")
                  : t("exerciseUnsnoozeConfirmationMessage")}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeSnoozeDialog} color="inherit" sx={{ '&:hover': { bgcolor: 'error.main', color: 'white' } }}>
                {t("cancel")}
              </Button>
              <Button
                onClick={handleConfirmSnoozeChange}
                color="primary"
                variant="contained"
              >
                {t("proceed")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snooze Success Snackbar */}
          <Snackbar
            open={!!snoozeSuccess}
            autoHideDuration={4000}
            onClose={() => setSnoozeSuccess("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnoozeSuccess("")}
              severity="success"
              variant="filled"
            >
              {snoozeSuccess}
            </Alert>
          </Snackbar>

          {/* Snooze Error Snackbar */}
          <Snackbar
            open={!!snoozeError}
            autoHideDuration={6000}
            onClose={() => setSnoozeError("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnoozeError("")}
              severity="error"
              variant="filled"
            >
              {snoozeError}
            </Alert>
          </Snackbar>

          <Backdrop
            open={loadingSnooze}
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.modal + 10,
              backdropFilter: "blur(2px)",
            }}
          >
            <CircularProgress color="inherit" size={48} thickness={4} />
          </Backdrop>
        </>
      )}
    </>
  );
};

export default UserAccountPopover;
